import { useEffect, useRef } from 'react';

import type { MarketApps } from '@/features/Dashboard/types';
import { useDashboard } from '@/hooks/api/dashboard';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';

import { selectInitialDashboardName, selectIsInitialized } from '../model/dashboardEdit.selectors';
import { resetDraft, setAllDashboardsData } from '../model/dashboardEdit.slice';
import {
  initializeDashboardEditThunk,
  reinitializeDraftThunk
} from '../model/dashboardEdit.thunks';

type UseDashboardEditInitParams = {
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  activeTabIndex?: number;
};

/**
 * Initializes dashboard edit draft from React Query cache.
 * Runs once on mount, resets on unmount.
 * Handles dashboard switching by reinitializing if the dashboard changes.
 */
export const useDashboardEditInit = ({
  dashboardName,
  initialDashboardName,
  dashboardConfig,
  activeTabIndex = 0
}: UseDashboardEditInitParams) => {
  const dispatch = useAppDispatch();
  const isInitialized = useAppSelector(selectIsInitialized);
  const currentEditingDashboard = useAppSelector(selectInitialDashboardName);

  // Track if we're currently reinitializing to prevent double dispatch
  const isReinitializingRef = useRef(false);

  // Fetch dashboard structure from React Query
  const { data: dashboards, isLoading } = useDashboard();

  // Initialize draft on mount or reinitialize on dashboard switch
  useEffect(() => {
    if (!dashboards) return;

    const serverData = dashboards[initialDashboardName];
    if (!serverData) return;

    // Case 1: First initialization
    if (!isInitialized) {
      // Store all dashboards data in Redux for cross-dashboard move operations
      dispatch(setAllDashboardsData({ data: dashboards }));

      // Use thunk for initialization to keep hook clean
      dispatch(
        initializeDashboardEditThunk({
          dashboardName,
          initialDashboardName,
          dashboardConfig,
          currentDashboardTabs: serverData,
          tabNames: Object.keys(serverData),
          activeTab: activeTabIndex
        })
      );
      return;
    }

    // Case 2: Dashboard changed while editing - reinitialize
    // This handles when user navigates to a different dashboard while in edit mode
    if (
      currentEditingDashboard &&
      currentEditingDashboard !== initialDashboardName &&
      !isReinitializingRef.current
    ) {
      isReinitializingRef.current = true;

      dispatch(
        reinitializeDraftThunk({
          dashboardName: initialDashboardName,
          activeTab: activeTabIndex,
          allDashboardsData: dashboards
        })
      ).finally(() => {
        isReinitializingRef.current = false;
      });
    }
  }, [
    dashboards,
    dashboardName,
    initialDashboardName,
    dashboardConfig,
    activeTabIndex,
    dispatch,
    isInitialized,
    currentEditingDashboard
  ]);

  // Reset draft on unmount
  useEffect(() => {
    return () => {
      dispatch(resetDraft());
    };
  }, [dispatch]);

  // Draft is considered stale if initialized but for a different dashboard
  const isDraftStale =
    isInitialized && currentEditingDashboard && currentEditingDashboard !== initialDashboardName;

  return { isInitialized: isInitialized && !isDraftStale, isLoading };
};
